import { useFormContext, Controller } from 'react-hook-form';
import FormErrorMessage from '../../../../helpers/FormErrorMessage';
import OptionList from '../../../../helpers/options/OptionList';
import SearchableSelect from '../../../../helpers/searchableSelect/SearchableSelect';
import { Bill } from './CreateBill';
import classes from './CreateBill.module.css';
import React from 'react';

type BillInfoSectionProps = {
  vendors: VendorOptionWithPaymentTerm[];
};

const BillInfoSection = ({ vendors }: BillInfoSectionProps) => {
  const { control, register, watch, setValue, clearErrors } =
    useFormContext<Bill>();

  //set due date based on vendors payment terms when bill date changes
  const handleBillDateChange = (e: React.ChangeEvent<HTMLInputElement>, field) => {
    field.onChange(e);
    const newBillDate = e.target.valueAsDate;
    const vendorId = watch('vendorId');
    const selectedVendor = vendors.find(
      v => v.vendorId === Number(vendorId),
    );
    if (
      selectedVendor &&
      selectedVendor.daysToPay &&
      newBillDate
    ) {
      newBillDate.setDate(
        newBillDate.getDate() + selectedVendor.daysToPay,
      );
      setValue(
        'dueDate',
        newBillDate.toISOString().split('T')[0],
      );
      clearErrors('dueDate');
    }
  };

  return (
    <section className={classes.billHeader}>
      <div className={classes.vendor}>
        <label htmlFor="vendorId">Vendor</label>
        <Controller
          name="vendorId"
          control={control}
          rules={{ required: 'A vendor is required.' }}
          render={({ field, fieldState }) => (
            <>
              <SearchableSelect
                {...field}
                placeholderText="-- Select Vendor --"
                onChange={value => field.onChange(value)}
                value={field.value}
              >
                <OptionList
                  optionData={vendors}
                  attributeID="vendorId"
                  attributeName="vendorName"
                />
              </SearchableSelect>
              <FormErrorMessage errorMessage={fieldState.error?.message} />
            </>
          )}
        />
        <label htmlFor="billName">Bill Name</label>
        <Controller
          name="billName"
          control={control}
          rules={{
            required: 'A bill name is required.',
            maxLength: {
              value: 21,
              message: 'Bill name cannot exceed 21 characters.',
            },
          }}
          render={({ field, fieldState }) => (
            <>
              <input {...field} id="billName" type="text" />
              <FormErrorMessage errorMessage={fieldState.error?.message} />
            </>
          )}
        />
      </div>
      <div className={classes.dates}>
        <label htmlFor="billDate">Bill Date</label>
        <Controller
          name="billDate"
          control={control}
          rules={{ required: 'A bill date is required.' }}
          render={({ field, fieldState }) => (
            <>
              <input
                {...field}
                id="billDate"
                type="date"
                onChange={e => handleBillDateChange(e, field)}
              />
              <FormErrorMessage errorMessage={fieldState.error?.message} />
            </>
          )}
        />
        <label htmlFor="dueDate">Due Date</label>
        <Controller
          name="dueDate"
          control={control}
          rules={{
            required: 'A due date is required.',
            min: {
              value: watch('billDate')
                ? new Date(watch('billDate')).toISOString().split('T')[0]
                : undefined,
              message: 'Due date cannot be before bill date.',
            },
          }}
          render={({ field, fieldState }) => (
            <>
              <input {...field} id="dueDate" type="date" />
              <FormErrorMessage errorMessage={fieldState.error?.message} />
            </>
          )}
        />
      </div>
      <div className={classes.note}>
        <label htmlFor="note">Note</label>
        <textarea {...register('note')} id="note" />
      </div>
    </section>
  );
};

export default BillInfoSection;
